// Дашборд — pagina de start, cu widget-uri, ca la scalpx.ru.
//
// Spre deosebire de ScalpX (unde majoritatea widget-urilor sunt demo), aici umplem
// ce putem cu date reale din piață: liderii de creștere/scădere, oborotul total,
// monedele cu spike de volum și densitățile urmărite acum.

import { el, clear, bus, fmtVolume, fmtPct, fmtCount, debounce } from '../core/util.js';
import { iconMoneda } from '../ui/coin-icon.js';
import { session } from '../core/session.js';
import { guestGate } from '../ui/guest-gate.js';
import { icon } from '../ui/icons.js';
import { market, settings } from '../core/state.js';
import { sparkline, baseTicker } from '../ui/components.js';

export class DashboardSection {
  constructor(app) {
    this.app = app;
    this.unsubs = [];
  }

  async mount(host) {
    this.root = el('div', { class: 'dash' });
    host.append(this.root);

    // Pagina de intrare, până la autentificare: spune ce e înăuntru și cum se intră.
    // Dashboard-ul plin are sens doar când există un „tu" pentru care e personalizat.
    if (!session.authenticated) {
      this.root.append(guestGate());
      this.offSession = bus.on('session:change', () => { if (session.authenticated) this.#rebuild(); });
      return;
    }
    this.#build();
    this.unsubs.push(bus.on('market:aggregates', debounce(() => this.#refresh(), 800)));
    this.unsubs.push(bus.on('market:sparklines', () => this.#refresh()));
    this.#refresh();
    this.#loadDensity();
  }

  /** După autentificare, dashboard-ul se construiește pe loc, fără reîncărcare. */
  #rebuild() {
    this.offSession?.();
    this.offSession = null;
    clear(this.root);
    this.#build();
    this.unsubs.push(bus.on('market:aggregates', debounce(() => this.#refresh(), 800)));
    this.unsubs.push(bus.on('market:sparklines', () => this.#refresh()));
    this.#refresh();
    this.#loadDensity();
  }

  unmount() {
    this.offSession?.();
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
    this.root?.remove();
  }

  #build() {
    this.root.append(
      el('div', { class: 'dash-head' }, [
        el('div', {}, [
          el('h1', { class: 'dash-title' }, [
            el('span', { text: 'Привет, ' }),
            el('span', { class: 'dash-title-accent', text: '@trader' }),
          ]),
          el('p', { class: 'dash-sub', text: 'Рынок сейчас — сводка по всем биржам. Нажмите «Скринер», чтобы работать с графиками.' }),
        ]),
        el('button', {
          class: 'dash-cta',
          onclick: () => this.app.navigate('/screener/'),
        }, [icon('chartLine', 15), el('span', { text: 'ОТКРЫТЬ СКРИНЕР' })]),
      ]),
    );

    this.statsEl = el('div', { class: 'dash-stats' });
    this.gainersEl = this.#listCard('Лидеры роста', 'fa-arrow-trend-up', 'up');
    this.losersEl = this.#listCard('Лидеры падения', 'fa-arrow-trend-down', 'down');
    this.spikesEl = this.#listCard('Всплески объёма', 'fa-fire', 'spike');
    this.densityEl = this.#listCard('Плотности в стакане', 'rows', 'density');

    this.root.append(
      this.statsEl,
      el('div', { class: 'dash-grid' }, [
        this.gainersEl.card, this.losersEl.card, this.spikesEl.card, this.densityEl.card,
      ]),
      el('div', { class: 'dash-grid dash-grid-2' }, [
        this.#toolsCard(),
        this.#aboutCard(),
      ]),
    );
  }

  #listCard(title, iconName, kind) {
    const body = el('div', { class: 'dash-list' });
    const card = el('section', { class: `dash-card dash-card-${kind}` }, [
      el('div', { class: 'dash-card-head' }, [
        icon(iconName, 14),
        el('h2', { text: title }),
      ]),
      body,
    ]);
    return { card, body };
  }

  #toolsCard() {
    const tools = [
      { label: 'Сетка', hint: 'Десятки графиков одним экраном', icon: 'grid', href: '/screener/' },
      { label: 'Список наблюдения', hint: 'Один график + watchlist', icon: 'eye', href: '/screener/coins/' },
      { label: 'Радар объёмов', hint: 'Куда заходят деньги', icon: 'radar', href: '/screener/volume-radar/' },
      { label: 'Терминал плотностей', hint: 'Крупные лимитки в стакане', icon: 'rows', href: '/screener/density/' },
      { label: 'Календарь листингов', hint: 'Новые монеты на биржах', icon: 'calendar', href: '/screener/listings/' },
      { label: 'Настройки', hint: 'Алерты, горячие клавиши', icon: 'userGear', href: '/screener/settings/' },
    ];
    return el('section', { class: 'dash-card' }, [
      el('div', { class: 'dash-card-head' }, [icon('rocket', 14), el('h2', { text: 'Инструменты' })]),
      el('div', { class: 'dash-tools' }, tools.map((t) => el('button', {
        class: 'dash-tool',
        onclick: () => this.app.navigate(t.href),
      }, [
        el('span', { class: 'dash-tool-icon' }, [icon(t.icon, 16)]),
        el('span', { class: 'dash-tool-text' }, [
          el('span', { class: 'dash-tool-label', text: t.label }),
          el('span', { class: 'dash-tool-hint', text: t.hint }),
        ]),
        icon('chevronRight', 11),
      ]))),
    ]);
  }

  #aboutCard() {
    const rows = [
      ['Биржи', 'Binance · Bybit · OKX · Bitget · Gate · MEXC — спот и фьючерсы'],
      ['Данные', 'Свечи и стаканы идут напрямую с бирж в браузер, без задержки на сервере'],
      ['Алерты', 'Уровни проверяются на сервере — сработают даже с закрытой вкладкой'],
      ['Рисунки', '14 инструментов, магнит к O/H/L/C, сохраняются по монете'],
    ];
    return el('section', { class: 'dash-card' }, [
      el('div', { class: 'dash-card-head' }, [icon('info', 14), el('h2', { text: 'Как это работает' })]),
      el('div', { class: 'dash-about' }, rows.map(([k, v]) => el('div', { class: 'dash-about-row' }, [
        el('span', { class: 'dash-about-key', text: k }),
        el('span', { class: 'dash-about-val', text: v }),
      ]))),
    ]);
  }

  // ------------------------------------------------------------------- date

  #refresh() {
    const rows = market.list().filter((r) => (r.quoteVolume || 0) > 1_000_000);
    if (!rows.length) return;

    const totalVolume = rows.reduce((a, r) => a + (r.quoteVolume || 0), 0);
    const totalTrades = rows.reduce((a, r) => a + (r.trades || 0), 0);
    const rising = rows.filter((r) => (r.change24h || 0) > 0).length;
    const btc = market.get('BTCUSDT');

    clear(this.statsEl).append(
      this.#stat('Оборот 24ч', fmtVolume(totalVolume), 'coins'),
      this.#stat('Сделок 24ч', fmtCount(totalTrades), 'bolt'),
      this.#stat('Монет в работе', String(rows.length), 'layerGroup'),
      this.#stat('Растут / падают', `${rising} / ${rows.length - rising}`,
        'fa-arrow-trend-up', rising >= rows.length / 2 ? 'up' : 'down'),
      this.#stat('BTC', btc ? fmtPct(btc.change24h) : '—', 'fa-bitcoin-sign',
        (btc?.change24h || 0) >= 0 ? 'up' : 'down'),
    );

    const byChange = [...rows].sort((a, b) => (b.change24h || 0) - (a.change24h || 0));
    this.#fillList(this.gainersEl.body, byChange.slice(0, 7));
    this.#fillList(this.losersEl.body, byChange.slice(-7).reverse());

    const bySpike = [...rows]
      .filter((r) => Number.isFinite(r.volTrend))
      .sort((a, b) => (b.volTrend || 0) - (a.volTrend || 0));
    this.#fillList(this.spikesEl.body, bySpike.slice(0, 7), 'spike');
  }

  #stat(label, value, iconName, tone = '') {
    return el('div', { class: 'dash-stat' }, [
      el('span', { class: 'dash-stat-icon' }, [icon(iconName, 14)]),
      el('span', { class: 'dash-stat-body' }, [
        el('span', { class: `dash-stat-value ${tone}`, text: value }),
        el('span', { class: 'dash-stat-label', text: label }),
      ]),
    ]);
  }

  #fillList(host, rows, mode = 'change') {
    clear(host);
    if (!rows.length) {
      host.append(el('div', { class: 'dash-empty', text: 'Данные загружаются…' }));
      return;
    }
    for (const row of rows) {
      const change = Number(row.change24h) || 0;
      const right = mode === 'spike'
        ? el('span', { class: 'dash-row-mult', text: `×${(1 + (row.volTrend || 0) / 100).toFixed(1)}` })
        : el('span', { class: `dash-row-change ${change >= 0 ? 'up' : 'down'}`, text: fmtPct(change) });

      host.append(el('button', {
        class: 'dash-row',
        onclick: () => {
          settings.set('sx_coins_symbol', row.symbol);
          this.app.navigate('/screener/coins/');
        },
      }, [
        iconMoneda(row.symbol, 18),
        el('span', { class: 'dash-row-sym', text: baseTicker(row.symbol) }),
        el('span', { class: 'dash-row-spark' }, [sparkline(market.spark(row.symbol), { width: 60, height: 18 })]),
        el('span', { class: 'dash-row-vol', text: fmtVolume(row.quoteVolume) }),
        right,
      ]));
    }
  }

  async #loadDensity() {
    try {
      const data = await (await fetch('/api/density?minSize=250000&maxDistance=2&limit=7')).json();
      const host = this.densityEl.body;
      clear(host);
      if (!data.walls?.length) {
        host.append(el('div', { class: 'dash-empty', text: 'Крупных лимиток рядом с ценой сейчас нет' }));
        return;
      }
      for (const wall of data.walls) {
        host.append(el('button', {
          class: `dash-row dash-row-wall ${wall.side}`,
          onclick: () => {
            settings.set('sx_coins_symbol', wall.symbol);
            this.app.navigate('/screener/density/');
          },
        }, [
          iconMoneda(wall.symbol, 18),
          el('span', { class: 'dash-row-sym', text: baseTicker(wall.symbol) }),
          el('span', { class: 'dash-row-side', text: wall.side === 'bid' ? 'покупка' : 'продажа' }),
          el('span', { class: 'dash-row-vol', text: fmtVolume(wall.sizeUsd) }),
          el('span', {
            class: `dash-row-change ${wall.distance >= 0 ? 'up' : 'down'}`,
            text: `${wall.distance >= 0 ? '+' : ''}${wall.distance.toFixed(2)}%`,
          }),
        ]));
      }
    } catch { /* scanner-ul poate să nu fie gata */ }
  }
}
